import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import toast from 'react-hot-toast';

const ReportGenerator = ({ templates, onGenerate }) => {
  const [generationType, setGenerationType] = useState('single'); // 'single' or 'bulk'
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [academicSession, setAcademicSession] = useState('');
  const [term, setTerm] = useState('');
  const [includeComments, setIncludeComments] = useState(true);
  const [includePsychomotor, setIncludePsychomotor] = useState(true);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch classes
      const classesRes = await adminAPI.getClasses();
      setClasses(classesRes.data);
      
      // Fetch academic sessions
      const sessionsRes = await adminAPI.getAcademicSessions();
      setSessions(sessionsRes.data);
      
      // Fetch templates if not passed as prop
      if (!templates || templates.length === 0) {
        const templatesRes = await adminAPI.getResultTemplates();
        // Set default template if available
        const defaultTemplate = templatesRes.data.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (academicSession) {
      fetchTermsForSession(academicSession);
    }
  }, [academicSession]);

  useEffect(() => {
    if (selectedClass && academicSession && term) {
      fetchStudentsForClass(selectedClass, academicSession, term);
    }
  }, [selectedClass, academicSession, term]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = students.filter(student =>
        student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.reg_no.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(students);
    }
  }, [searchQuery, students]);

  const fetchTermsForSession = async (sessionId) => {
    try {
      const response = await adminAPI.getSessionTerms(sessionId);
      setTerms(response.data);
    } catch (error) {
      toast.error('Failed to load terms');
    }
  };

  const fetchStudentsForClass = async (classId, sessionId, termId) => {
    try {
      const response = await adminAPI.getEnrolledStudents({
        class_id: classId,
        academic_session_id: sessionId,
        term_id: termId
      });
      setStudents(response.data);
      setFilteredStudents(response.data);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };



  const handleGenerateReport = async () => {
    // if (!selectedTemplate) {
    //   toast.error('Please select a template');
    //   return;
    // }

    if (!academicSession || !term) {
      toast.error('Please select academic session and term');
      return;
    }

    // const config = {
    //   template_id: selectedTemplate,
    //   academic_session_id: academicSession,
    //   term_id: term,
    //   include_comments: includeComments,
    //   include_psychomotor: includePsychomotor
    // };



      if (!selectedTemplate) {
    toast.error('Please select a template');
    return;
  }

  const config = {
    template_id: parseInt(selectedTemplate), // Ensure it's a number
    academic_session_id: parseInt(academicSession),
    term_id: parseInt(term),
    include_comments: includeComments,
    include_psychomotor: includePsychomotor
  };

    if (generationType === 'single') {
      if (!selectedStudent) {
        toast.error('Please select a student');
        return;
      }
      config.student_reg = selectedStudent;
      config.type = 'single';
    } else {
      if (!selectedClass) {
        toast.error('Please select a class');
        return;
      }
      config.class_id = selectedClass;
      config.type = 'bulk';
      config.output_format = 'zip';
    }

    try {
      await onGenerate(config);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const handleQuickGenerate = async (studentReg) => {
    if (!selectedTemplate) {
      toast.error('Please select a template first');
      return;
    }

    const config = {
      template_id: selectedTemplate,
      academic_session_id: academicSession || sessions[0]?.id,
      term_id: term || terms[0]?.id,
      student_reg: studentReg,
      include_comments: includeComments,
      include_psychomotor: includePsychomotor,
      type: 'single'
    };

    try {
      await onGenerate(config);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generation Type Selection */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Report Generation</h3>
          <p className="text-sm text-gray-500">Generate individual or bulk reports</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Configuration */}
            <div className="space-y-6">
              {/* Generation Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Generation Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setGenerationType('single')}
                    className={`p-4 border rounded-lg text-center ${
                      generationType === 'single'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className="fas fa-user-graduate text-2xl mb-2"></i>
                    <div className="font-medium">Single Student</div>
                    <p className="text-xs text-gray-500 mt-1">Generate for one student</p>
                  </button>
                  
                  <button
                    onClick={() => setGenerationType('bulk')}
                    className={`p-4 border rounded-lg text-center ${
                      generationType === 'bulk'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className="fas fa-users text-2xl mb-2"></i>
                    <div className="font-medium">Bulk Generation</div>
                    <p className="text-xs text-gray-500 mt-1">Generate for entire class</p>
                  </button>
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Template *
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select a template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                      {template.is_default && ' (Default)'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose the template for report layout
                </p>
              </div>

              {/* Academic Session & Term */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Academic Session *
                  </label>
                  <select
                    value={academicSession}
                    onChange={(e) => setAcademicSession(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select session</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.academic_session}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Term *
                  </label>
                  <select
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                    disabled={!academicSession}
                  >
                    <option value="">Select term</option>
                    {terms.map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.term}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Class Selection (for bulk or filtering) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class {generationType === 'bulk' && '*'}
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required={generationType === 'bulk'}
                >
                  <option value="">Select class</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Options */}
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-comments"
                    checked={includeComments}
                    onChange={(e) => setIncludeComments(e.target.checked)}
                    className="h-4 w-4 text-primary-600"
                  />
                  <label htmlFor="include-comments" className="ml-2 text-sm text-gray-700">
                    Include teacher comments
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-psychomotor"
                    checked={includePsychomotor}
                    onChange={(e) => setIncludePsychomotor(e.target.checked)}
                    className="h-4 w-4 text-primary-600"
                  />
                  <label htmlFor="include-psychomotor" className="ml-2 text-sm text-gray-700">
                    Include psycho-motor assessment
                  </label>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateReport}
                disabled={!selectedTemplate || !academicSession || !term || (generationType === 'bulk' && !selectedClass) || (generationType === 'single' && !selectedStudent)}
                className={`w-full btn-primary py-3 text-lg ${
                  (!selectedTemplate || !academicSession || !term || (generationType === 'bulk' && !selectedClass) || (generationType === 'single' && !selectedStudent))
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                <i className="fas fa-file-pdf mr-2"></i>
                {generationType === 'single' ? 'Generate Student Report' : 'Generate Class Reports (ZIP)'}
              </button>
            </div>

            {/* Right Column: Student Selection/List */}
            <div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-4">
                  {generationType === 'single' ? 'Select Student' : 'Class Students'}
                  {students.length > 0 && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({students.length} students)
                    </span>
                  )}
                </h4>

                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name or registration..."
                      className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2"
                    />
                    <i className="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                  </div>
                </div>

                {/* Student List */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                      <div
                        key={student.student_reg}
                        className={`p-3 border rounded-lg ${
                          selectedStudent === student.student_reg
                            ? 'bg-primary-50 border-primary-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{student.name}</div>
                            <div className="text-sm text-gray-500">
                              {student.student_reg} • {student.class_name}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {generationType === 'single' ? (
                              <button
                                onClick={() => setSelectedStudent(student.student_reg)}
                                className={`px-3 py-1 rounded ${
                                  selectedStudent === student.student_reg
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                Select
                              </button>
                            ) : (
                              <button
                                onClick={() => handleQuickGenerate(student.student_reg)}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                title="Generate individual report"
                              >
                                <i className="fas fa-download"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {selectedClass && academicSession && term ? (
                        <>
                          <i className="fas fa-users-slash text-3xl mb-3 opacity-50"></i>
                          <p>No students found in this class for selected session/term</p>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-graduate text-3xl mb-3 opacity-50"></i>
                          <p>Select a class, session, and term to view students</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Student Info */}
                {generationType === 'single' && selectedStudent && (
                  <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="font-medium">Selected Student</h5>
                        <p className="text-sm text-gray-600">
                          {students.find(s => s.student_reg === selectedStudent)?.name || selectedStudent}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedStudent('')}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setSelectedClass('');
                      setSelectedStudent('');
                      setSearchQuery('');
                    }}
                    className="btn-secondary py-2"
                  >
                    <i className="fas fa-redo mr-2"></i>
                    Clear Selection
                  </button>
                  
                  <button
                    onClick={() => {
                      // Select all students for bulk operations
                      if (generationType === 'bulk' && students.length > 0) {
                        toast.success(`Preparing ${students.length} students for bulk generation`);
                      }
                    }}
                    className="bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                  >
                    <i className="fas fa-check-double mr-2"></i>
                    Select All
                  </button>
                </div>
              </div>

              {/* Generation Statistics */}
              <div className="mt-6 bg-white border rounded-lg p-4">
                <h4 className="font-medium mb-3">Generation Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">
                      {generationType === 'single' ? '1' : students.length}
                    </div>
                    <div className="text-sm text-gray-600">Reports</div>
                  </div>
                  
                  <div className="text-center p-3 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">
                      {selectedTemplate ? '✓' : '✗'}
                    </div>
                    <div className="text-sm text-gray-600">Template</div>
                  </div>
                </div>
                
                <div className="mt-4 text-sm text-gray-500">
                  <p className="flex items-center">
                    <i className="fas fa-info-circle mr-2"></i>
                    {generationType === 'single'
                      ? 'Single PDF will be downloaded'
                      : 'All reports will be bundled in a ZIP file'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Generation History */}
      {/* <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => {
                // Generate for all classes
                toast.info('This feature will generate reports for all classes');
              }}
              className="p-4 border rounded-lg text-center hover:bg-gray-50"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-school text-purple-600 text-xl"></i>
              </div>
              <h4 className="font-medium">All Classes</h4>
              <p className="text-sm text-gray-500">Generate for entire school</p>
            </button>
            
            <button
              onClick={() => {
                // Generate for top performers
                toast.info('This feature will generate reports for top performers');
              }}
              className="p-4 border rounded-lg text-center hover:bg-gray-50"
            >
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-trophy text-yellow-600 text-xl"></i>
              </div>
              <h4 className="font-medium">Top Performers</h4>
              <p className="text-sm text-gray-500">Generate for top 10 students</p>
            </button>
            
            <button
              onClick={() => {
                // Preview generation
                toast.info('This feature will show a preview of the report');
              }}
              className="p-4 border rounded-lg text-center hover:bg-gray-50"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-eye text-blue-600 text-xl"></i>
              </div>
              <h4 className="font-medium">Preview Report</h4>
              <p className="text-sm text-gray-500">View sample report before generation</p>
            </button>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default ReportGenerator;